import { IcoRoute } from "assets/icons";
import IcoRoad from "assets/icons/ico-road";
import clsx from "clsx";
import { AlertCard, SelectedDisplay, SelectForm } from "components";
import { baseConfig } from "config";
import { ErrorHelper } from "helpers";
import { useDataModel } from "hooks/useDataModel";
import { HighwayConcession } from "models/HighwayConcession";
import { InterventionType } from "models/InterventionType";
import { InterventionTypeCategory } from "models/InterventionTypeCategory";
import {
	MutableRefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { toast } from "react-toastify";
import { InterventionTypeService } from "services/interventionType";
import { DocumentFolderTypesAndFileExtensions } from "services/types";
import DocumentTemplateModal from "../ApplicationDocuments/DocumentTemplateModal";
import { StepLoading } from "../layout";
import { ApplicationFields, ApplicationFormik } from "../types";

const menuScrollOffset = 30;

export default function Intervention({
	formik,
	onGoBack,
	saveDraftApplication,
	documentFolderTypesData,
	isLoadingFolderTypes,
	isSavingDraft
}: ApplicationFormik & {
	onGoBack: () => void;
	saveDraftApplication: () => void;
	documentFolderTypesData: DocumentFolderTypesAndFileExtensions | null;
	isLoadingFolderTypes: boolean;
	isSavingDraft: boolean;
}) {
	const { highwayConcession, interventionType, interventionTypeCategory } =
		formik.values;
	const interventionTypeService = useMemo(
		InterventionTypeService.getInstance,
		[]
	);
	const selectedHighway = useDataModel(highwayConcession, HighwayConcession);
	const selectedInterventionType = useDataModel(
		interventionType,
		InterventionType
	);
	const selectedInterventionTypeCategory: InterventionTypeCategory | null =
		useMemo(() => {
			return (
				selectedInterventionType?.categories?.find(
					(category) => category.id === interventionTypeCategory?.id
				) ?? null
			);
		}, [interventionTypeCategory, selectedInterventionType]);

	const [interventionTypes, setInterventionTypes] =
		useState<InterventionType[]>();
	const [isLoadingTypes, setIsLoadingTypes] = useState(false);
	const [isDocumentModalOpen, setIsDocumentModalOpen] =
		useState<boolean>(false);
	const requiredDocuments = useMemo(
		() =>
			documentFolderTypesData?.documentFolderTypes.filter(
				(folderType) => folderType.alwaysRequired
			),
		[documentFolderTypesData]
	);
	const optionalDocuments = useMemo(
		() =>
			documentFolderTypesData?.documentFolderTypes.filter(
				(folderType) => !folderType.alwaysRequired
			),
		[documentFolderTypesData]
	);

	useEffect(() => {
		if (!selectedHighway) {
			setInterventionTypes(undefined);
			return;
		}

		setIsLoadingTypes(true);
		interventionTypeService
			.listByHighwayConcession(selectedHighway.id)
			.then((response) => {
				setInterventionTypes(response);
			})
			.catch((e) => toast.error(ErrorHelper.getResponseErrorMessage(e)))
			.finally(() => setIsLoadingTypes(false));
	}, [selectedHighway?.id]);

	const resetCategory = () => {
		formik.setFieldValue(ApplicationFields.interventionTypeCategory, null);
	};

	const scrollableContainer = useRef<HTMLDivElement | null>(null);
	const typeSelectAnchor = useRef<HTMLDivElement | null>(null);
	const scrollToSelect = useCallback(
		(
			anchor: MutableRefObject<HTMLDivElement | null>,
			offset: number = menuScrollOffset
		) => {
			if (!anchor.current || !scrollableContainer.current) return;

			const anchorElement = anchor.current;
			const scrollableElement = scrollableContainer.current;
			const topPosition =
				anchorElement.getBoundingClientRect().top +
				scrollableElement.scrollTop -
				scrollableElement.getBoundingClientRect().top +
				offset;

			scrollableElement.scrollTo({
				top: topPosition,
				behavior: "smooth"
			});
		},
		[]
	);

	const interventionTypeHref = useMemo(() => {
		if (
			!baseConfig.highwayBaseUrl ||
			!selectedHighway?.concessionaire.websiteSlugString ||
			!selectedInterventionType?.websiteSlugString
		) {
			return "#";
		}
		return `${baseConfig.highwayBaseUrl}/${selectedHighway.concessionaire.websiteSlugString}/services-page/faixa-de-dominio/${selectedInterventionType.websiteSlugString}`;
	}, [
		baseConfig.highwayBaseUrl,
		selectedHighway?.concessionaire.websiteSlugString,
		selectedInterventionType?.websiteSlugString
	]);

	return (
		<div
			className="h-full overflow-y-auto flex flex-col items-center max-w-2xl"
			ref={scrollableContainer}
		>
			<div className="flex flex-col-reverse md:flex-col w-full">
				<div className="md:bg-neutral-high-200 w-full">
					<div className="my-4 md:mx-8 mx-6 rounded-lg p-4 md:pt-5 md:pb-6 bg-neutral-high-pure-50 flex items-center gap-4 border border-neutral-high-200">
						<SelectedDisplay
							text="Rodovia selecionada"
							selected={selectedHighway ? selectedHighway.toString() : ""}
							isIconVisible
							isEdit
							onGoBack={onGoBack}
							icon={<IcoRoad />}
						/>
					</div>
				</div>
				<div className="flex items-center px-8">
					<IcoRoute />
					<h1 className="my-4 text-lg font-semibold md:text-lg md:font-normal mx-1">
						Selecione o tipo de intervenção
					</h1>
				</div>
			</div>
			{isLoadingTypes && <StepLoading />}
			<div className="w-full md:mb-10">
				{interventionTypes && !isLoadingTypes && (
					<div className="mx-8 my-4 flex flex-col gap-6" ref={typeSelectAnchor}>
						<SelectForm
							name={ApplicationFields.interventionType}
							setFieldValue={formik.setFieldValue}
							options={interventionTypes?.map((type) => type.toOption())}
							value={selectedInterventionType?.toOption()}
							label="Tipo de intervenção"
							tooltip="Algumas intervenções possuem características específicas que podem impactar no prazo de análise e na documentação necessária para abrir a solicitação."
							onChange={() => {
								resetCategory();
							}}
							onMenuOpen={() => scrollToSelect(typeSelectAnchor)}
							menuPlacement="bottom"
							useOuterScroll
							menuPosition="absolute"
							error={formik.errors.interventionType}
							touched={formik.touched.interventionType}
						/>
						{!!selectedInterventionType?.categories?.length &&
							!isLoadingTypes && (
								<SelectForm
									name={ApplicationFields.interventionTypeCategory}
									setFieldValue={formik.setFieldValue}
									options={selectedInterventionType?.categories?.map((type) =>
										type.toOption()
									)}
									value={selectedInterventionTypeCategory?.toOption()}
									label="Categoria"
									error={formik.errors.interventionTypeCategory}
									touched={formik.touched.interventionTypeCategory}
								/>
							)}

						{selectedInterventionType && (
							<AlertCard>
								<div className="font-bold mt-2">
									{selectedInterventionType.name}
								</div>
								<div
									className={clsx({
										"mt-2 text-sm": selectedInterventionType.description
									})}
								>
									{selectedInterventionType.description}
								</div>
								<div
									className={clsx({
										"mt-4 text-sm": selectedInterventionType.description
									})}
								>
									Acesse nosso site:{" "}
									<a
										href={interventionTypeHref}
										target="_blank"
										className="underline font-bold break-all md:break-normal"
										rel="noreferrer"
									>
										rodovias.motiva.com.br/faixadedominio
									</a>
									{` para conferir as normas e regras para este tipo de intervenção.`}
								</div>
								<button
									type="button"
									className="mt-4 text-sm underline font-bold p-0"
									onClick={async () => {
										setIsDocumentModalOpen(true);
										await saveDraftApplication();
									}}
								>
									Consulte os documentos
								</button>
							</AlertCard>
						)}
						<DocumentTemplateModal
							isOpen={isDocumentModalOpen}
							setIsOpen={setIsDocumentModalOpen}
							requiredDocuments={requiredDocuments ?? []}
							optionalDocuments={optionalDocuments ?? []}
							sectionTitle={selectedInterventionType?.name ?? ""}
							isLoading={isLoadingFolderTypes || isSavingDraft}
						/>
					</div>
				)}
			</div>
		</div>
	);
}
